// feature/eva-litisoft — the pilot's home. Operator-first: at a glance, am I
// ready, what's ready, what's running, what happened — plus Eva narrating the
// safe-first flow (review → simulate → confirm → upload) so someone who isn't
// Nico can run this with confidence. Athena-native cards/tokens; the dry-run
// path is always the primary, calm action.
import React from "react";

const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

// The single "what should I do next" card — the heart of the operator UX.
function NextStep({ pulse, goTo }) {
  if (!pulse) return null;
  let tone = "mint", title, body, cta = null;

  if (!pulse.vaultConfigured) {
    tone = "danger";
    title = "No puedo trabajar todavía";
    body = "Falta configurar BOT_CRED_KEY en el servidor (la bóveda donde guardo las credenciales). Esto lo hace el administrador en el despliegue.";
  } else if (!pulse.credsReady) {
    tone = "warning";
    title = "Me faltan las credenciales de PREVISORA";
    body = "Sin ellas no puedo entrar a LITISOFT. Guárdalas en Configuración — quedan cifradas y nunca se muestran de vuelta.";
    cta = { label: "Ir a Configuración", to: "config" };
  } else if (!pulse.worker?.paired) {
    tone = "info";
    title = "Necesito un worker para mover el navegador";
    body = "El worker es un programita que corre en tu computador o en una máquina dedicada y es el que realmente pilotea LITISOFT. Descárgalo y empareja uno.";
    cta = { label: "Descargar y emparejar", to: "config" };
  } else if (pulse.activeRun) {
    tone = "mint";
    const r = pulse.activeRun;
    title = r.dry_run ? `Estoy simulando la corrida #${r.id}` : `Estoy radicando la corrida #${r.id}`;
    body = r.dry_run
      ? "Es una simulación: reviso y preparo todo, pero no subo nada a LITISOFT. Mírala en vivo y revisa qué haría con cada caso."
      : "Carga real en curso — estoy escribiendo en el portal. Puedes pausarme o detenerme entre casos desde el Monitor.";
    cta = { label: "Ver en el Monitor", to: "monitor" };
  } else if (pulse.eligible > 0) {
    tone = "mint";
    title = `Tengo ${pulse.eligible} caso${pulse.eligible === 1 ? "" : "s"} listo${pulse.eligible === 1 ? "" : "s"}`;
    body = "Te recomiendo simular primero: te muestro exactamente qué radicaría en cada caso, sin tocar el portal. Si todo se ve bien, lo aprobamos y subo de verdad.";
    cta = { label: "Revisar y simular", to: "cola" };
  } else {
    tone = "success";
    title = "Todo al día";
    body = "No hay casos pendientes por radicar ahora mismo. Cuando lleguen tutelas que coincidan con un único proceso y tengan hechos, las verás aquí.";
    cta = { label: "Ver reconciliación", to: "reconciliacion" };
  }

  return (
    <div className={"banner-info " + (tone === "mint" ? "mint" : tone)} style={{ flexDirection: "column", alignItems: "stretch", gap: 6 }}>
      <div style={{ fontWeight: 700, fontSize: 14 }}>{title}</div>
      <div style={{ fontSize: 13, lineHeight: 1.5 }}>{body}</div>
      {cta && (
        <div style={{ marginTop: 6 }}>
          <button onClick={() => goTo(cta.to)} style={{ height: 32 }}>{cta.label}</button>
        </div>
      )}
    </div>
  );
}

function StatTile({ value, label, color }) {
  return (
    <div className="card" style={{ padding: "12px 16px", minWidth: 120, flex: "1 1 120px" }}>
      <div className="tnum" style={{ fontSize: 24, fontWeight: 700, color: color || "var(--text-primary)" }}>
        {value ?? "—"}
      </div>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
    </div>
  );
}

function ActiveRunCard({ run, goTo }) {
  const p = pct(run.completed_items, run.total_items);
  return (
    <div className="card" style={{ padding: 16, borderLeft: "3px solid var(--accent-mint)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <strong>Corrida #{run.id}</strong>
        <span className="badge" style={{ background: run.dry_run ? "var(--color-info-bg)" : "var(--color-danger-bg)", color: run.dry_run ? "var(--color-info-ink)" : "var(--color-danger-ink)" }}>
          {run.dry_run ? "Simulación" : "Carga real"}
        </span>
        <span className="muted" style={{ fontSize: 12 }}>{run.status}</span>
        <span className="right" />
        <button className="ghost sm" onClick={() => goTo("monitor")}>Ver en el Monitor →</button>
      </div>
      <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ flex: 1, height: 8, background: "var(--bg-subtle)", borderRadius: 4, overflow: "hidden" }}>
          <div style={{ width: `${p}%`, height: "100%", background: "var(--accent-mint)", transition: "width var(--motion-base) var(--motion-spring-soft)" }} />
        </div>
        <span className="tnum muted" style={{ fontSize: 12 }}>{run.completed_items}/{run.total_items}</span>
      </div>
    </div>
  );
}

function LastRunCard({ run, goTo }) {
  const ok = run.status === "done";
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span className="muted" style={{ fontSize: 12 }}>Última corrida</span>
        <strong>#{run.id}</strong>
        <span className="badge" style={{ background: run.dry_run ? "var(--color-info-bg)" : "var(--color-danger-bg)", color: run.dry_run ? "var(--color-info-ink)" : "var(--color-danger-ink)" }}>
          {run.dry_run ? "Simulación" : "Carga real"}
        </span>
        <span className="badge" style={{ background: ok ? "var(--color-success-bg)" : "var(--color-danger-bg)", color: ok ? "var(--color-success-ink)" : "var(--color-danger-ink)" }}>
          {ok ? "Terminó" : run.status === "failed" ? "Falló" : "Detenida"}
        </span>
        <span className="right" />
        <button className="ghost sm" onClick={() => goTo("historial")}>Ver historial →</button>
      </div>
      <div className="muted tnum" style={{ fontSize: 12, marginTop: 6 }}>
        {run.completed_items}/{run.total_items} casos procesados
      </div>
    </div>
  );
}

// Compact, calming "how this works" — for whoever is piloting for the first time.
function ComoFunciona() {
  const steps = [
    ["1 · Reviso", "Clasifico las tutelas pendientes contra los procesos existentes. Sólo paso las que coinciden con un único caso y tienen hechos — los acumulados nunca se radican solos."],
    ["2 · Simulo", "Hago una corrida en seco: preparo todo y te muestro qué radicaría en cada caso, sin tocar LITISOFT."],
    ["3 · Confirmas", "Revisas la simulación. Si algo se ve raro, lo paramos ahí."],
    ["4 · Radico", "Con tu visto bueno, subo de verdad y guardo el número LITISOFT que me devuelve cada caso."],
  ];
  return (
    <div className="card" style={{ padding: 16 }}>
      <div style={{ fontWeight: 600, marginBottom: 10 }}>Cómo trabajamos, paso a paso</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
        {steps.map(([h, b]) => (
          <div key={h} className="glass-section" style={{ padding: "10px 12px" }}>
            <div style={{ fontWeight: 600, fontSize: 12.5, color: "var(--accent-mint)" }}>{h}</div>
            <div className="muted" style={{ fontSize: 12, marginTop: 4, lineHeight: 1.45 }}>{b}</div>
          </div>
        ))}
      </div>
      <div className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
        Puedes pausarme o detenerme entre casos en cualquier momento desde el Monitor.
      </div>
    </div>
  );
}

export default function ResumenTab({ pulse, reload, goTo }) {
  if (!pulse) {
    return (
      <div className="col" style={{ gap: 12 }}>
        <div className="skeleton" style={{ width: "100%", height: 84, borderRadius: 10 }} />
        <div style={{ display: "flex", gap: 12 }}>
          {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton" style={{ flex: 1, height: 64, borderRadius: 12 }} />)}
        </div>
        <div className="skeleton" style={{ width: "100%", height: 140, borderRadius: 12 }} />
      </div>
    );
  }

  return (
    <div className="col" style={{ gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: -8 }}>
        <button className="ghost sm" onClick={reload} title="Actualizar">↻ Actualizar</button>
      </div>

      <NextStep pulse={pulse} goTo={goTo} />

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <StatTile value={pulse.eligible} label="Listos para radicar" color="var(--color-success)" />
        <StatTile value={pulse.ambiguous} label="Ambiguos / acumulados" color="var(--color-warning)" />
        <StatTile value={pulse.missingHechos} label="Sin hechos" />
        <StatTile value={pulse.totalPending} label="Pendientes en total" />
      </div>

      {pulse.activeRun && <ActiveRunCard run={pulse.activeRun} goTo={goTo} />}
      {!pulse.activeRun && pulse.lastRun && <LastRunCard run={pulse.lastRun} goTo={goTo} />}

      <ComoFunciona />
    </div>
  );
}
